/**
 * Google Analytics (GA4, gtag.js) — Consent Mode v2.
 *
 * Základní načtení + výchozí consent ('denied') je v index.html, aby běželo co nejdřív.
 * Tady řešíme jen aktualizaci souhlasu (po volbě v cookie liště) a SPA pageview.
 */
const GA_ID = 'G-CH870WPMME'

/** Google Ads (konverzní sledování) — config tag se načítá v index.html spolu s GA. */
const GADS_ID = 'AW-18262243729'

/**
 * Konverzní labely z Google Ads (Nástroje a nastavení → Konverze → klikni na akci →
 * sekce „Nastavení tagu" → hodnota `send_to` je ve tvaru 'AW-18262243729/AbCd1eFgHi'
 * — sem doplň jen část ZA lomítkem). Dokud je placeholder, konverze se NEodpaluje.
 */
const GADS_CONVERSION_LABELS = {
  register: 'REGISTER_LABEL',
  purchase: 'TXSnCKneisQcEJHzjoRE',
} as const

/** Klíč pro odloženou konverzi přežívající full reload (např. OAuth callback). */
const GADS_PENDING_KEY = 'gads_conv_pending'

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void
    dataLayer?: unknown[]
  }
}

/** Po volbě v cookie liště aktualizuje Consent Mode (granted = plný tracking, jinak denied). */
export function updateGoogleConsent(granted: boolean): void {
  const v = granted ? 'granted' : 'denied'
  window.gtag?.('consent', 'update', {
    ad_storage: v,
    ad_user_data: v,
    ad_personalization: v,
    analytics_storage: v,
  })
}

/** Odešle GA4 page_view (SPA navigace — gtag automaticky pošle jen první načtení). */
export function gaPageview(path: string): void {
  if (!window.gtag) return
  window.gtag('event', 'page_view', {
    page_path: path,
    page_location: window.location.href,
    page_title: document.title,
    send_to: GA_ID,
  })
}

/**
 * Odešle Google Ads konverzi. Souhlas řeší Consent Mode v2 (ad_storage) nastavený
 * v index.html — bez souhlasu Google jen modeluje, neukládá cookies, takže firing
 * tu negatujeme ručně (na rozdíl od Sklik). Placeholder label se nikdy neodešle.
 */
export function googleAdsConversion(
  kind: keyof typeof GADS_CONVERSION_LABELS,
  opts: { value?: number; currency?: string; transactionId?: string } = {},
): void {
  const label = GADS_CONVERSION_LABELS[kind]
  if (!window.gtag || label.endsWith('_LABEL')) return // label nedoplněn → neodpalovat
  window.gtag('event', 'conversion', {
    send_to: `${GADS_ID}/${label}`,
    ...(opts.value != null ? { value: opts.value, currency: opts.currency || 'CZK' } : {}),
    ...(opts.transactionId ? { transaction_id: opts.transactionId } : {}),
  })
}

/** Zafrontuje konverzi přežívající full reload (OAuth callback dělá window.location.assign). */
export function queueGoogleAdsConversion(kind: keyof typeof GADS_CONVERSION_LABELS): void {
  try { sessionStorage.setItem(GADS_PENDING_KEY, kind) } catch { /* ignore */ }
}

/** Po startu appky odpálí odloženou Google Ads konverzi, pokud nějaká čeká. Volat jednou v App. */
export function flushPendingGoogleAdsConversion(): void {
  try {
    const kind = sessionStorage.getItem(GADS_PENDING_KEY)
    if (kind === 'register' || kind === 'purchase') {
      sessionStorage.removeItem(GADS_PENDING_KEY)
      googleAdsConversion(kind)
    }
  } catch { /* ignore */ }
}
